import * as React from 'react';
import type { SelectedOptionValue } from 'react-select-search';
import { useSelect } from 'react-select-search';
import Input from '../Input/Input';
import List from '../List/List';
import ListItem from '../List/ListItem/ListItem';
import Tooltip from '../Tooltip/Tooltip';
import type { Distribute } from '../../../modules/ts.utils';
import cn from '~modules/cn';
import ChevronDownIcon from '../../../assets/icons/v2design/navigation/chevron-down.svg';
import ChevronUpIcon from '../../../assets/icons/v2design/navigation/chevron-up.svg';
import AddIcon from '../../../assets/icons/v2design/basics/add.svg';
import './dropdown-search.scss';

export type DropdownSearchOption<T> = {
    name: string;
    value: T;
    searchPatterns?: string[];
    renderName?: React.ReactNode;
    leftView?: React.ReactElement;
    rightView?: React.ReactNode;
    disabled?: boolean;
    bottomText?: React.ReactNode;
    className?: string;
    tip?: string;
    tipOverflow?: boolean;
};
export type DropdownSearchValue = SelectedOptionValue;

type BaseProps<T> = {
    placeholder?: string;
    id?: string;
    emptyMessage?: string;
    addPrefix?: string;
    disabled?: boolean;
    options: DropdownSearchOption<T>[];
    filterOptions?: (options: DropdownSearchOption<T>[]) => (query: string) => DropdownSearchOption<T>[];
    closeOnSelect?: boolean;
    className?: string;
    onChangeSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    showAddButton?: boolean;
    onAddClicked?: (value: string) => void;
    searchItem?: (option: DropdownSearchOption<T>, optionProps: $TSFixMe, curValue: $TSFixMe, highlighted: number, index: number) => React.ReactElement;
};

type Props<T> =
    | (BaseProps<T> & {
          multiple?: false;
          value: T;
          onChange?: (selectedValue: T) => void;
      })
    | (BaseProps<T> & {
          multiple: true;
          value: Distribute<T>;
          onChange?: (selectedValue: Distribute<T>) => void;
      });

const ADD_ITEM_VALUE = '__ADD_ITEM';

const defaultFilterOpts =
    <T extends string | number>(options: DropdownSearchOption<T>[]) =>
    (query: string) =>
        options.filter((opt) => [opt.name, ...(opt.searchPatterns ?? [])].some((str) => str.toLowerCase().includes(query.toLowerCase())));

// as any used as react-select-search TS props are wrong compared to their source code

const DropdownSearch = <T extends string | number>(props: Props<T>): JSX.Element => {
    const { onChange, onAddClicked, multiple, searchItem, addPrefix = 'Add', emptyMessage } = props;
    const inputRef = React.useRef(null) as React.MutableRefObject<HTMLInputElement | null>;
    const [inputValue, setInputValue] = React.useState('');
    const options = React.useMemo(
        () =>
            props.showAddButton &&
            inputValue &&
            !props.options?.find(({ name, searchPatterns = [] }) => [name, ...searchPatterns].some((str) => str === inputValue))
                ? (props.options ?? []).concat({
                      name: [addPrefix, inputValue].filter(Boolean).join(' '),
                      // we enforce same type of values but private add_item_value here is string
                      value: ADD_ITEM_VALUE as $TSFixMe,
                      leftView: <AddIcon />,
                      className: 'list-item--add'
                  })
                : props.options,
        [props.options, props.showAddButton, inputValue, addPrefix]
    );
    const onChangeHandler = React.useCallback<(value: (typeof props)['value']) => void>(
        (value) => {
            let isAddValueClicked = false;
            if (multiple && Array.isArray(value)) {
                isAddValueClicked = (value as string[]).includes(ADD_ITEM_VALUE);
            } else {
                isAddValueClicked = value === ADD_ITEM_VALUE;
            }
            if (isAddValueClicked) {
                onAddClicked?.(inputValue);
            } else {
                onChange?.(value as $TSFixMe);
            }
        },
        [multiple, onAddClicked, inputValue, onChange]
    );
    const [snapshot, valueProps, optionProps] = useSelect({
        ...props,
        options,
        onChange: onChangeHandler as $TSFixMe,
        filterOptions: (props.filterOptions as $TSFixMe) ?? defaultFilterOpts,
        search: true,
        value: (typeof props.value === 'undefined' || props.value === null) && (props.placeholder || props.multiple) ? ('' as $TSFixMe) : props.value
    });

    const highlighted = snapshot.highlighted as unknown as number;
    const curValue = snapshot.value as unknown as typeof props.value;
    const curOptions = snapshot.options as unknown as typeof props.options;
    const displayValue = curValue === ADD_ITEM_VALUE ? props.value : snapshot.displayValue || props.value;
    return (
        <div className={cn('dropdown-search', props.className)}>
            <div className="dropdown-search__input-container">
                <Input
                    {...(valueProps as $TSFixMe)}
                    ref={(input) => {
                        inputRef.current = input;
                        valueProps.ref.current = input;
                    }}
                    id={props.id}
                    autoComplete="none"
                    placeholder={props.placeholder}
                    value={snapshot.focus ? inputValue : displayValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        props.onChangeSearch?.(e);
                        (valueProps.onChange as $TSFixMe)(e);
                    }}
                    onBlur={(e) => {
                        setInputValue('');
                        (valueProps.onBlur as $TSFixMe)(e);
                    }}
                />
                {snapshot.focus ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </div>
            <Tooltip
                tooltipProps={{ visible: snapshot.focus && (curOptions.length === 0 ? Boolean(emptyMessage) : true) }}
                popperProps={{
                    placement: 'bottom',
                    modifiers: [
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['top']
                            }
                        }
                    ],
                    onFirstUpdate(popper) {
                        // popper not really precise positioned to the input - fix it
                        const tooltipLeft = popper.elements?.popper.getBoundingClientRect().left;
                        const inputLeft = inputRef.current?.getBoundingClientRect().left;
                        if (tooltipLeft && inputLeft && tooltipLeft !== inputLeft) {
                            popper.elements!.popper.style.marginLeft = `${inputLeft - tooltipLeft}px`;
                        }
                    }
                }}
                offset={[0, -2]}
                for={inputRef}
                appearanceClassNames={['tooltip--no-background', 'tooltip--no-padding', 'tooltip--no-max-width']}
                className="dropdown-search__tooltip"
            >
                <div className="dropdown-search__list-container" style={{ width: inputRef.current?.getBoundingClientRect().width }}>
                    <List>
                        {curOptions.length ? (
                            curOptions.map((option, index) => (
                                <li key={option.value}>
                                    <button
                                        type="button"
                                        {...(optionProps as $TSFixMe)}
                                        disabled={option.disabled}
                                        className="dropdown-search__item-wrapper"
                                        value={option.value}
                                    >
                                        {searchItem ? (
                                            searchItem(option, optionProps, curValue, highlighted, index)
                                        ) : (
                                            <ListItem
                                                Tag="div"
                                                size="full"
                                                text={option.renderName ?? option.name}
                                                bottomText={option.bottomText}
                                                selected={Array.isArray(curValue) ? (curValue as $TSFixMe).includes(option.value) : option.value === curValue}
                                                className={cn(option.className, { 'list-item--highlighted': highlighted === index })}
                                                leftView={option.leftView}
                                                disabled={option.disabled}
                                                rightView={option.rightView}
                                                dataTip={option.tip ?? option.name}
                                                dataTipOverflow={option.tipOverflow ?? true}
                                                dataTipDelay={0}
                                            />
                                        )}
                                    </button>
                                </li>
                            ))
                        ) : (
                            <li className="dropdown-search__empty-message">{emptyMessage}</li>
                        )}
                    </List>
                </div>
            </Tooltip>
        </div>
    );
};

export default DropdownSearch;
